// Ecommerce settings — the merchant's `settings.ecommerce_settings` object.
//
// Single source of truth for what each admin field means. Every reader in the
// app (bootstrap, home pages, product modal, order cancellation, analytics)
// goes through these helpers instead of poking at raw snake_case keys.
//
// Fields used by the storefront:
//   theme                      industry skin key (e.g. "restaurant")
//   hide_out_of_stock_items    hide sold-out products in merchandised rows
//   display_ingredients        show the ingredients block in the product modal
//   top_selling_items          [{ id | item_id, name }] — `name` is the row title
//   top_selling_categories     [{ id, name }]           — `name` is the row title
//   email, support_contact_number, whatsapp_number      footer / legal contact
//   facebook_url, instagram_url, twitter_url, youtube_url   social icons
//   privacy_policy, terms_and_conditions, about_us      legal page bodies
//   enable_analytics, analytics_type, analytics_id      GA4 / GTM
//   enable_customer_cancellation (+ window fields)      see utils/orderCancellation
import { formatList, formatPhoneList } from '@core/utils/runtimeBrand';

const SOCIAL_FIELDS = {
  facebook: 'facebook_url',
  instagram: 'instagram_url',
  twitter: 'twitter_url',
  youtube: 'youtube_url',
};

/** Industry skin key chosen in the admin ('' when unset). */
export function getIndustryTheme(ecommerce) {
  return String(ecommerce?.theme || '')
    .toLowerCase()
    .trim();
}

/** { facebook, instagram, twitter, youtube } with empty ones dropped, or null. */
export function getSocialLinks(ecommerce) {
  const source = ecommerce || {};
  const links = Object.fromEntries(
    Object.entries(SOCIAL_FIELDS)
      .map(([key, field]) => [key, source[field] || source[key] || ''])
      .filter(([, url]) => url),
  );
  return Object.keys(links).length ? links : null;
}

/** Contact details shown in the footer and legal pages (already formatted). */
export function getContactDetails(ecommerce, { fallbackEmail = '' } = {}) {
  const source = ecommerce || {};
  return {
    email: formatList(source.email || fallbackEmail || ''),
    supportPhone: formatPhoneList(source.support_contact_number || []),
    whatsappNumber: formatPhoneList(source.whatsapp_number || []),
  };
}

/** Legal page bodies (HTML strings). */
export function getPolicies(ecommerce) {
  const source = ecommerce || {};
  return {
    privacyPolicy: source.privacy_policy || '',
    termsAndConditions: source.terms_and_conditions || '',
    aboutUs: source.about_us || '',
    faqs: source.faqs || '',
  };
}

const itemIdOf = (entry) =>
  entry && typeof entry === 'object' ? (entry.id ?? entry.item_id) : entry;

/**
 * Admin-picked best sellers, in admin order: [{ id, name, group }]. `name` is the
 * row title the admin typed ('' if none); `group` is that title or the default
 * (entries with the same title form one row).
 */
export function getTopSellingItems(ecommerce, { defaultGroup = "Today's Best Sellers" } = {}) {
  const list = ecommerce?.top_selling_items;
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  return list
    .map((entry) => ({
      id: itemIdOf(entry),
      name: (entry && typeof entry === 'object' && entry.name) || '',
      group: (entry && typeof entry === 'object' && entry.name) || defaultGroup,
    }))
    .filter(({ id }) => id != null && id !== '' && !seen.has(String(id)) && seen.add(String(id)));
}

/** Unique ids of the admin-picked best sellers, in admin order. */
export function getTopSellingItemIds(ecommerce) {
  return getTopSellingItems(ecommerce).map(({ id }) => id);
}

/** Admin-picked top categories, in admin order: [{ id, name, group }]. */
export function getTopSellingCategories(ecommerce, { defaultGroup = 'Top Categories' } = {}) {
  const list = ecommerce?.top_selling_categories;
  if (!Array.isArray(list)) return [];
  return list
    .filter((entry) => entry?.id != null && entry.id !== '')
    .map((entry) => ({ id: entry.id, name: entry.name || '', group: entry.name || defaultGroup }));
}

/** Group resolved records by the admin row title, keeping admin order. */
export function groupByTopSelling(refs, records) {
  const byId = new Map(records.map((record) => [String(record.id), record]));
  const groups = new Map();
  refs.forEach(({ id, group }) => {
    const record = byId.get(String(id));
    if (!record) return;
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group).push(record);
  });
  return [...groups].map(([title, items]) => ({ title, items }));
}

/** Everything the storefront reads from ecommerce_settings, normalised. */
export function parseEcommerceSettings(ecommerce, { fallbackEmail } = {}) {
  const raw = ecommerce || {};
  return {
    raw,
    industryTheme: getIndustryTheme(raw),
    hideOutOfStock: Boolean(raw.hide_out_of_stock_items),
    displayIngredients: raw.display_ingredients == true,
    topSellingItems: getTopSellingItems(raw),
    topSellingCategories: getTopSellingCategories(raw),
    contact: getContactDetails(raw, { fallbackEmail }),
    socialLinks: getSocialLinks(raw),
    policies: getPolicies(raw),
    analytics: {
      enabled: Boolean(raw.enable_analytics),
      type: raw.analytics_type || '',
      id: raw.analytics_id || '',
    },
    customerCancellation: Boolean(raw.enable_customer_cancellation),
  };
}
